<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Activity</span>
    </template>

    <template #actions>
      <router-link v-if="playId" :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
    </template>

    <template #filters>
      <div style="width: 240px">
        <SearchSelect
          v-model="filters.signal_name"
          :options="signalFilterOptions"
          placeholder="All triggers"
          search-placeholder="Search triggers..."
          clear-label="All triggers"
        />
      </div>

      <div style="width: 240px">
        <SearchSelect
          v-model="filters.account_domain"
          :optionsFunction="searchAccountsByDomain"
          :maxResults="20"
          placeholder="All accounts"
          search-placeholder="Search accounts..."
          clear-label="All accounts"
        />
      </div>

      <!-- Agent filter is meaningless when scoped to a single play (the
           play already owns exactly one agent). -->
      <div v-if="!playId" style="width: 240px">
        <SearchSelect
          v-model="filters.agent_id"
          :options="agentFilterOptions"
          placeholder="All agents"
          search-placeholder="Search agents..."
          clear-label="All agents"
        />
      </div>

      <div style="width: 170px">
        <SearchSelect
          v-model="filters.status"
          :options="statusFilterOptions"
          placeholder="All statuses"
          search-placeholder="Search statuses..."
          clear-label="All statuses"
        />
      </div>
    </template>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="listPanel">
      <div v-if="loading && items.length === 0" class="listPanelEmpty">Loading…</div>

      <div v-else-if="items.length === 0" class="listPanelEmpty">No activity.</div>

      <div v-else class="sections">
        <section v-for="section in daySections" :key="section.dayKey" class="daySection">
          <div class="dayHeader">
            <div class="dayTitle">{{ section.dayLabel }}</div>
            <div class="dayMeta">{{ section.items.length }} activit{{ section.items.length === 1 ? 'y' : 'ies' }}</div>
          </div>

          <div class="daySeparator" aria-hidden="true"></div>

          <div class="tableWrap">
            <table class="table">
              <colgroup>
                <col class="colTrigger" />
                <col class="colAccount" />
                <col class="colAgents" />
                <col class="colResults" />
              </colgroup>
              <tbody>
                <tr
                  v-for="item in section.items"
                  :key="rowKey(item)"
                  class="activityRow"
                  :class="{ selected: rowKey(item) === selectedRowKey }"
                  :data-row-key="rowKey(item)"
                  @click="selectRow(item)"
                >
                  <td class="triggerCell">
                    <button v-if="item.row_kind === 'signal'" class="linkBtn" @click.stop="openSignal(item)">
                      <HeaderWithIcon
                        :domain="item.signal_icon_type === 'favicon' ? item.signal_icon_name : ''"
                        :iconType="item.signal_icon_type === 'favicon' ? 'image' : 'hero'"
                        :iconName="item.signal_icon_name"
                        :iconStyle="item.signal_icon_style || 'solid'"
                        :iconColor="item.signal_icon_color || '#d97706'"
                        :label="displaySignalName(item)"
                        :secondaryText="displaySignalSubtitle(item)"
                      />
                    </button>
                    <HeaderWithIcon
                      v-else
                      iconType="hero"
                      :iconName="runSourceMeta(item).iconName"
                      :iconStyle="runSourceMeta(item).iconStyle"
                      :iconColor="runSourceMeta(item).color"
                      :label="runSourceMeta(item).label"
                      :secondaryText="formatTimePart(item.occurred_at)"
                    />
                  </td>
                  <td>
                    <button class="linkBtn" @click="openAccount(item)">
                      <HeaderWithIcon
                        :domain="item.account_domain"
                        :label="item.account_name || item.account_domain"
                        :secondaryText="item.account_domain"
                      />
                    </button>
                  </td>
                  <td class="agentsCell">
                    <div v-if="(item.agent_runs || []).length === 0" class="emptyLabel">No agents</div>
                    <div v-else class="agentsList">
                      <button
                        v-for="run in (item.agent_runs || [])"
                        :key="run.id"
                        class="agentPill"
                        @click.stop="openRun(run.id)"
                      >
                        <HeroIcon
                          :name="run.agent_icon_name || 'UserCircleIcon'"
                          :iconStyle="run.agent_icon_style || 'outline'"
                          :color="run.agent_icon_color || '#6b7280'"
                          :size="14"
                        />
                        <span class="agentName">{{ run.agent_name || 'Agent' }}</span>
                        <PulseDot v-if="run.status === 'running'" :size="8" color="var(--color-text-tertiary)" aria-label="running" />
                        <span v-else class="agentStatusText" :class="statusClass(run.status)">{{ run.status }}</span>
                      </button>
                    </div>
                  </td>
                  <td class="resultsCell">
                    <div v-if="outcomePills(item).length > 0" class="badges">
                      <button
                        v-for="pill in outcomePills(item)"
                        :key="`${pill.type}-${pill.id}`"
                        class="outcomePill"
                        :class="{ clickable: pill.clickable, hasColor: !!pill.color }"
                        :style="pill.color ? { color: pill.color } : {}"
                        @click.stop="handlePillClick(pill)"
                      >
                        <AppIcon class="pillIcon" :name="pill.iconKey" :size="14" />
                        <span class="pillLabel">{{ pill.label }}</span>
                      </button>
                    </div>
                    <div v-else class="emptyLabel">No results</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div v-if="nextOffset && !loading" ref="scrollTrigger" class="scrollTrigger"></div>
      <div v-if="loading && items.length > 0" class="loadingMore">Loading more...</div>
    </div>

    <SignalDetailModal
      v-if="selectedSignal"
      :signal="selectedSignal"
      :accountIdByDomain="accountIdByDomain"
      :agentById="agentById"
      @close="selectedSignal = null"
    />

    <ReachoutModal
      v-if="selectedReachout"
      :reachout="selectedReachout"
      @close="selectedReachout = null"
    />

    <CrmModificationModal
      v-if="selectedCrm"
      :modification="selectedCrm"
      @close="selectedCrm = null"
      @updated="onCrmUpdated"
    />

  </PageChrome>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT } from '../../drawer/drawerTypes'
import AppIcon from '../../components/AppIcon.vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'
import HeroIcon from '../../components/HeroIcon.vue'
import PulseDot from '../../components/PulseDot.vue'
import SignalDetailModal from '../../components/SignalDetailModal.vue'
import ReachoutModal from '../../components/ReachoutModal.vue'
import CrmModificationModal from '../../components/CrmModificationModal.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { getIcon } from '../../services/iconRegistry'
import { formatSignalBaseName } from '../../services/signal_format'
import { googleFaviconUrl } from '../../services/favicons'
import { apiFetch } from '../../services/api'
import { onActiveTagChange } from '../../services/reactiveState'
import { fetchReachout, fetchCrmModification } from '../../services/outcomeItems'
import { ensureSelectedId, navigateSelectedId } from '../../services/accountEngagement'
import { useChatScope } from '../composables/useChatScope'

const DISPLAY_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const router = useRouter()
const route = useRoute()
const { setContext: setDrawerContext } = useContextDrawer()
const { setScope, clearScope } = useChatScope()

// Sales-play scoping: when the route is /sales-plays/:id/activity, every
// query and chat-scope assertion is restricted to that play. Workspace
// view (/activity) leaves playId empty.
const playId = computed(() => String(route.params.id || ''))
const playName = ref('')

const items = ref<any[]>([])
const loading = ref(false)
const error = ref('')
const nextOffset = ref<number | null>(null)

const agents = ref<any[]>([])
const signalRegistry = ref<any[]>([])
const selectedSignal = ref<any>(null)
const selectedReachout = ref<any>(null)
const selectedCrm = ref<any>(null)
const scrollTrigger = ref<HTMLElement | null>(null)
let scrollObserver: IntersectionObserver | null = null
let pollInterval: ReturnType<typeof setInterval> | null = null

const filters = ref({
  status: '',
  signal_name: '',
  account_domain: '',
  agent_id: '',
})

function applyRouteQueryToFilters() {
  const q = route?.query || {}
  if (q?.signal_name) filters.value.signal_name = String(q.signal_name)
  if (q?.account_domain) filters.value.account_domain = String(q.account_domain)
  if (q?.agent_id) filters.value.agent_id = String(q.agent_id)
}

async function syncRouteQueryFromFilters() {
  const nextQuery = { ...route.query }
  if (filters.value.signal_name) nextQuery.signal_name = filters.value.signal_name
  else delete nextQuery.signal_name
  if (filters.value.account_domain) nextQuery.account_domain = filters.value.account_domain
  else delete nextQuery.account_domain
  if (filters.value.agent_id) nextQuery.agent_id = filters.value.agent_id
  else delete nextQuery.agent_id
  await router.replace({ path: route.path, query: nextQuery })
}

function formatTimePart(ts: string | Date) {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { timeZone: DISPLAY_TZ, hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}

function buildQuery(offset: number) {
  const params = new URLSearchParams()
  params.set('limit', '100')
  params.set('offset', String(offset || 0))
  params.set('include_test', 'false')
  if (filters.value.status) params.set('status', filters.value.status)
  if (filters.value.signal_name) params.set('signal_name', filters.value.signal_name)
  if (filters.value.account_domain) params.set('account_domain', filters.value.account_domain)
  if (filters.value.agent_id) params.set('agent_id', filters.value.agent_id)
  if (playId.value) params.set('sales_play_id', playId.value)
  return params.toString()
}

async function searchAccountsByDomain(query: string) {
  const qs = new URLSearchParams({ q: query, limit: '20' })
  const resp = await apiFetch(`/accounts/paginated?${qs}`)
  const accts = Array.isArray(resp?.items) ? resp.items : []
  return accts.map((a: any) => ({
    value: String(a.domain || ''),
    label: String(a.name || a.domain || '').trim() || String(a.domain || ''),
    domain: String(a.domain || ''),
  }))
}

const agentFilterOptions = computed(() =>
  (agents.value || [])
    .map((a) => ({
      value: String(a?.id || ''),
      label: String(a?.name || '').trim(),
      iconName: a?.icon_name || null,
      iconStyle: a?.icon_style || 'outline',
      iconColor: a?.icon_color || null,
    }))
    .filter((o) => o.value),
)

const statusFilterOptions = computed(() => [
  { value: 'queued', label: 'queued' },
  { value: 'running', label: 'running' },
  { value: 'succeeded', label: 'succeeded' },
  { value: 'failed', label: 'failed' },
  { value: 'stopped', label: 'stopped' },
])

const signalFilterOptions = computed(() => {
  const options: any[] = [
    { value: '__any_signal__', label: 'Any Signal', iconKey: 'signal', iconColor: '#d97706' },
    { value: '__manual__', label: 'Manual', iconKey: 'manual', iconColor: '#9ca3af' },
  ]
  const signals = (signalRegistry.value || [])
    .map((s) => {
      const name = String(s?.name || '').trim()
      if (!name) return null
      const displayName = String(s?.display_name || '').trim() || name
      return {
        value: name,
        label: displayName,
        icon: s?.icon_type === 'favicon' && s?.icon_name ? googleFaviconUrl(s.icon_name, { size: 32 }) : null,
        iconName: s?.icon_type === 'hero' ? s?.icon_name : null,
        iconStyle: s?.icon_style || 'outline',
        iconColor: s?.icon_color || null,
      }
    })
    .filter(Boolean) as any[]
  return [...options, ...signals]
})

async function loadFilterOptions() {
  try {
    const [agentResp, signalResp] = await Promise.all([
      apiFetch('/agents?include_sales_play_owned=true'),
      apiFetch('/signals/registry'),
    ])
    agents.value = Array.isArray((agentResp as any)?.agents) ? (agentResp as any).agents : []
    signalRegistry.value = Array.isArray((signalResp as any)?.signals) ? (signalResp as any).signals : []
  } catch {
    agents.value = []
    signalRegistry.value = []
  }
}

async function loadItems(reset = false, background = false) {
  if (!background) loading.value = true
  error.value = ''
  try {
    const off = reset ? 0 : Number(nextOffset.value || 0)
    if (!reset && !nextOffset.value) return

    const resp: any = await apiFetch(`/agent_activity/items?${buildQuery(off)}`)
    const newItems = resp?.items || []

    if (reset && background && items.value.length > 0) {
      // Silent poll: prepend only items we don't already have, so the
      // existing list (and the user's scroll) isn't disturbed.
      const existingKeys = new Set(items.value.map((item) => rowKey(item)))
      const toAdd = newItems.filter((item: any) => !existingKeys.has(rowKey(item)))
      if (toAdd.length > 0) items.value = [...toAdd, ...items.value]
    } else {
      items.value = newItems
      nextOffset.value = resp?.next_offset ?? null
      await ensureSelectedRow()
    }
  } catch (e: any) {
    if (!background) error.value = e?.message || 'Failed to load activity'
  } finally {
    if (!background) loading.value = false
  }
}

async function applyFilters() {
  await syncRouteQueryFromFilters()
  await loadItems(true)
}

watch(filters, async () => { await applyFilters() }, { deep: true })

function openRun(id: string) {
  const runId = String(id || '').trim()
  if (!runId) return
  router.push(`/agent-runs/${encodeURIComponent(runId)}`)
}

function openAccount(item: any) {
  const id = String(item?.account_id || '').trim()
  if (!id) return
  router.push(`/accounts/${encodeURIComponent(id)}`)
}

function rowKey(item: any) {
  if (item?.row_kind === 'signal' && item?.signal_id) return `signal:${item.signal_id}`
  if (item?.row_kind === 'run' && Array.isArray(item?.agent_runs) && item.agent_runs[0]?.id) return `run:${item.agent_runs[0].id}`
  return `${item?.row_kind || 'row'}:${item?.occurred_at || ''}`
}

const selectedRowKey = computed(() => String(route.query.selected || '').trim())

async function replaceSelectedQuery(nextKey: string) {
  const query: any = { ...route.query }
  if (nextKey) query.selected = nextKey
  else delete query.selected
  await router.replace({ path: route.path, query })
}

function selectRow(item: any) {
  void replaceSelectedQuery(rowKey(item))
}

function displaySignalName(item: any) {
  const name = String(item?.signal_name || '')
  if (name === 'gmail.inbound_email' || name === 'gmail.outbound_email') {
    const subject = String(item?.signal_description?.subject || '').trim()
    if (subject) return subject
  }
  if (item?.signal_display_name) return item.signal_display_name
  return formatSignalBaseName({ name, description: item?.signal_description || {} })
}

function displaySignalSubtitle(item: any) {
  return formatTimePart(item?.occurred_at)
}

function openSignal(item: any) {
  selectedSignal.value = {
    id: item?.signal_id,
    occurred_at: item?.occurred_at,
    name: item?.signal_name,
    description: item?.signal_description || {},
    account_domain: item?.account_domain,
    account_name: item?.account_name,
    source_type: item?.signal_source_type,
    source_label: item?.signal_source_label,
    triggered_count: Number((item?.agent_runs || []).length || 0),
  }
}

// Source-meta resolution. Same shape as the legacy page, except sales_play
// runs read as "Approved on {play}" — there is no signal trigger for these
// in the new world; an approved candidate is what kicked the run off.
const RUN_SOURCE_META: Record<string, { label: string; iconKey: string; color: string }> = {
  manual:         { label: 'manual',    iconKey: 'manual',     color: '#9ca3af' },
  signal:         { label: 'signal',    iconKey: 'signal',     color: '#f59e0b' },
  sequence:       { label: 'sequence',  iconKey: 'sequence',   color: '#8b5cf6' },
  sales_play:     { label: 'sales play', iconKey: 'sales_play', color: '#10b981' },
  source:         { label: 'source',    iconKey: 'trigger',    color: '#0ea5e9' },
  filter:         { label: 'qualify',   iconKey: 'filter',     color: '#0ea5e9' },
  trigger_action: { label: 'action',    iconKey: 'action',     color: '#10b981' },
  chat_agent:     { label: 'chat',      iconKey: 'chat_agent', color: '#8b5cf6' },
  revision_ui:    { label: 'revision',  iconKey: 'manual',     color: '#9ca3af' },
}

function buildRunSourceMeta(meta: { label: string; iconKey: string; color: string }, overrides?: { label?: string }) {
  const icon = getIcon(meta.iconKey)
  return {
    label: (overrides && overrides.label) || meta.label,
    iconKey: meta.iconKey,
    iconName: icon?.name || 'QuestionMarkCircleIcon',
    iconStyle: icon?.style || 'outline',
    color: meta.color,
  }
}

function runSourceMeta(item: any) {
  const raw = String(item?.run_source || '').trim().toLowerCase().replaceAll(' ', '_')
  if (!raw || raw === 'manual' || raw === 'run') return buildRunSourceMeta(RUN_SOURCE_META.manual)
  if (raw === 'trigger_signal' || raw === 'signal') return buildRunSourceMeta(RUN_SOURCE_META.signal)
  if (raw === 'trigger_sequence' || raw === 'sequence') return buildRunSourceMeta(RUN_SOURCE_META.sequence)
  if (raw === 'trigger_action') return buildRunSourceMeta(RUN_SOURCE_META.trigger_action)
  if (raw === 'source') return buildRunSourceMeta(RUN_SOURCE_META.source)
  if (raw === 'filter') return buildRunSourceMeta(RUN_SOURCE_META.filter)
  if (raw === 'chat_agent') return buildRunSourceMeta(RUN_SOURCE_META.chat_agent)
  if (raw === 'revision_ui') return buildRunSourceMeta(RUN_SOURCE_META.revision_ui)
  if (raw === 'sales_play' || raw === 'salesplay') {
    const playLabel = String(item?.sales_play_name || '').trim()
    return buildRunSourceMeta(
      RUN_SOURCE_META.sales_play,
      playLabel ? { label: playLabel } : undefined,
    )
  }
  return buildRunSourceMeta(RUN_SOURCE_META.manual)
}

function outcomePills(item: any) {
  const pills: any[] = []
  for (const ro of item?.reachouts || []) {
    const status = String(ro.status || '').toLowerCase()
    const needsAction = status === 'pending_approval'
    pills.push({ type: 'reachout', id: ro.id, iconKey: 'reachout', label: formatReachoutStatus(status), color: needsAction ? '#3b82f6' : undefined, clickable: true, data: ro })
  }
  for (const sig of item?.signals || []) {
    pills.push({ type: 'signal', id: sig.id, iconKey: 'signal', label: sig.display_name || sig.name || 'Signal', color: '#f59e0b', clickable: true, data: sig })
  }
  for (const crm of item?.crm_modifications || []) {
    const status = String(crm.status || '').toLowerCase()
    const needsAction = status === 'pending' || status === 'queued'
    pills.push({ type: 'crm', id: crm.id, iconKey: 'crm_update', label: formatCrmStatus(status), color: needsAction ? '#8b5cf6' : undefined, clickable: true, data: crm })
  }
  return pills
}

function formatReachoutStatus(status: string) {
  if (status === 'pending_approval') return 'Pending'
  if (status === 'sent') return 'Sent'
  if (status === 'delivered') return 'Delivered'
  if (status === 'opened') return 'Opened'
  if (status === 'clicked') return 'Clicked'
  if (status === 'replied') return 'Replied'
  if (status === 'bounced') return 'Bounced'
  if (status === 'failed') return 'Failed'
  return status
}

function formatCrmStatus(status: string) {
  if (status === 'pending') return 'Pending'
  if (status === 'queued') return 'Queued'
  if (status === 'applied') return 'Applied'
  if (status === 'failed') return 'Failed'
  return status
}

async function handlePillClick(pill: any) {
  if (!pill.clickable) return
  try {
    if (pill.type === 'reachout') {
      selectedReachout.value = await fetchReachout(pill.id)
    } else if (pill.type === 'signal') {
      selectedSignal.value = {
        ...pill.data,
        account_domain: items.value.find((i: any) => i.signals?.some((s: any) => s.id === pill.id))?.account_domain,
      }
    } else if (pill.type === 'crm') {
      selectedCrm.value = await fetchCrmModification(pill.id)
    }
  } catch (err) {
    console.error('Failed to fetch item details:', err)
  }
}

function onCrmUpdated() {
  selectedCrm.value = null
  loadItems(true)
}

function statusClass(status: string) {
  const s = String(status || '').toLowerCase()
  if (s === 'succeeded') return 'success'
  if (s === 'failed' || s === 'stopped') return 'danger'
  if (s === 'running' || s === 'queued') return 'warning'
  return ''
}

const accountIdByDomain = computed(() => {
  const map: Record<string, string> = {}
  for (const item of items.value || []) {
    const domain = String(item?.account_domain || '').trim()
    const id = String(item?.account_id || '').trim()
    if (domain && id && !map[domain]) map[domain] = id
  }
  return map
})

const agentById = computed(() => {
  const map: Record<string, any> = {}
  for (const a of agents.value || []) if (a?.id) map[String(a.id)] = a
  return map
})

function localDayKey(ts: Date | string | number) {
  const d = ts instanceof Date ? ts : new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const y = parts.find((p) => p.type === 'year')?.value
  const m = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value
  return `${y}-${m}-${day}`
}

function formatDayLabel(dayKey: string) {
  const [y, m, d] = String(dayKey || '').split('-').map((v) => Number(v || 0))
  if (!y || !m || !d) return dayKey
  const dt = new Date(y, m - 1, d)
  if (Number.isNaN(dt.getTime())) return dayKey
  const today = new Date()
  const todayKey = localDayKey(today)
  const yest = new Date()
  yest.setDate(yest.getDate() - 1)
  const yesterdayKey = localDayKey(yest)
  const base = dt.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
  if (dayKey === todayKey) return `Today — ${base}`
  if (dayKey === yesterdayKey) return `Yesterday — ${base}`
  return base
}

const daySections = computed(() => {
  const groupedByDay = new Map<string, any[]>()
  for (const item of items.value || []) {
    const date = new Date(item.occurred_at)
    if (Number.isNaN(date.getTime())) continue
    const dayKey = localDayKey(date)
    if (!dayKey) continue
    let dayItems = groupedByDay.get(dayKey)
    if (!dayItems) { dayItems = []; groupedByDay.set(dayKey, dayItems) }
    dayItems.push(item)
  }
  return Array.from(groupedByDay.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([dayKey, dayItems]) => {
      const sortedItems = dayItems.sort((a: any, b: any) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      return { dayKey, dayLabel: formatDayLabel(dayKey), items: sortedItems }
    })
})

const flatItems = computed(() => daySections.value.flatMap((s) => s.items))

async function ensureSelectedRow() {
  const ensuredKey = ensureSelectedId(flatItems.value, selectedRowKey.value, rowKey)
  if (ensuredKey !== selectedRowKey.value) {
    await replaceSelectedQuery(ensuredKey)
  }
}

watch(
  selectedRowKey,
  (key) => {
    if (!key) { setDrawerContext(null); return }
    const item = flatItems.value.find((i: any) => rowKey(i) === key)
    const accountId = String(item?.account_id || '').trim()
    if (accountId) setDrawerContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId })
    else setDrawerContext(null)
    nextTick(() => {
      try {
        const el = document.querySelector(`[data-row-key="${CSS.escape(key)}"]`)
        el?.scrollIntoView({ block: 'nearest' })
      } catch { /* ignore */ }
    })
  },
  { immediate: true },
)

function shouldHandleActivityArrowNavigation(event: KeyboardEvent) {
  if (!selectedRowKey.value) return false
  if (selectedSignal.value || selectedReachout.value || selectedCrm.value) return false
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return false
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return false
  const target = event.target instanceof Element ? event.target : null
  if (!target) return true
  if (target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], button.btn, [role="dialog"]')) return false
  return true
}

async function onWindowKeydown(event: KeyboardEvent) {
  if (!shouldHandleActivityArrowNavigation(event)) return
  const direction = event.key === 'ArrowDown' ? 'next' : 'prev'
  const nextKey = navigateSelectedId(flatItems.value, selectedRowKey.value, direction, rowKey)
  if (!nextKey || nextKey === selectedRowKey.value) return
  event.preventDefault()
  await replaceSelectedQuery(nextKey)
}

onActiveTagChange(() => loadItems(true))

onMounted(async () => {
  // Chat scope reflects the page's surface. Workspace for /activity, the
  // owning play for /sales-plays/:id/activity.
  if (playId.value) {
    setScope({ scopeType: 'sales_play', scopeSubjectId: playId.value, pageContext: { sales_play_id: playId.value } })
    try {
      const playResp: any = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
      playName.value = String(playResp?.name || '').trim()
    } catch {
      playName.value = ''
    }
  } else {
    setScope({ scopeType: 'workspace', scopeSubjectId: '' })
  }

  applyRouteQueryToFilters()
  await loadFilterOptions()
  await loadItems(true)
  await ensureSelectedRow()
  setupInfiniteScroll()

  pollInterval = setInterval(() => {
    if (!document.hidden && !loading.value) loadItems(true, true)
  }, 5000)

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('keydown', onWindowKeydown)
})

function handleVisibilityChange() {
  if (!document.hidden && !loading.value) loadItems(true, true)
}

onBeforeUnmount(() => {
  setDrawerContext(null)
  clearScope()
  if (scrollObserver) { scrollObserver.disconnect(); scrollObserver = null }
  if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('keydown', onWindowKeydown)
})

function setupInfiniteScroll() {
  if (scrollObserver) scrollObserver.disconnect()
  scrollObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      if (entry.isIntersecting && nextOffset.value && !loading.value) loadItems(false)
    },
    { rootMargin: '100px' },
  )
  watch(scrollTrigger, (el) => { if (el && scrollObserver) scrollObserver.observe(el) }, { immediate: true })
}
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  border-radius: var(--radius-md);
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-text-primary); }
.backArrow { transform: rotate(180deg); }

.btn {
  display: inline-flex;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-out);
}
.btn:hover { border-color: var(--color-border-strong); }

.error {
  color: var(--color-danger);
  margin-bottom: var(--space-3);
}

.listPanel {
  background: var(--color-bg-app);
}
.listPanelEmpty {
  padding: var(--space-7) 0;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--fs-sm);
}

.sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-3) 0;
}

.daySection { padding: 0; }
.dayHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
}
.dayTitle {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.dayMeta {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
.daySeparator {
  height: 1px;
  background: var(--color-border-subtle);
  margin-bottom: var(--space-2);
}

.tableWrap {
  overflow-x: auto;
  background: var(--color-bg-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
}
.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.colTrigger { width: 300px; }
.colAccount { width: 260px; }
.colAgents  { width: 260px; }
.colResults { width: auto; }
.table td {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border-subtle);
  vertical-align: top;
  min-width: 0;
}
.table tbody tr:last-child td { border-bottom: 0; }

.activityRow {
  transition: background var(--duration-fast) var(--ease-out);
  cursor: pointer;
}
.activityRow:hover > td { background: var(--color-bg-subtle); }
.activityRow.selected > td { background: var(--color-bg-subtle); }
.activityRow.selected > td:first-child {
  box-shadow: inset 3px 0 0 0 var(--color-accent);
}

.triggerCell, .agentsCell, .resultsCell { min-width: 0; }

.linkBtn {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.scrollTrigger { height: 1px; width: 100%; }
.loadingMore {
  padding: var(--space-5);
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}

.agentsList { display: flex; flex-wrap: wrap; gap: var(--space-1); }
.agentPill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 3px var(--space-2);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-full);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
  font-size: var(--fs-xs);
  transition: border-color var(--duration-fast) var(--ease-out);
}
.agentPill:hover { border-color: var(--color-accent); }
.agentName { font-weight: var(--fw-semibold); }
.agentStatusText {
  font-size: 11px;
  font-weight: var(--fw-medium);
  text-transform: lowercase;
}
.agentStatusText.success { color: var(--color-accent); }
.agentStatusText.warning { color: var(--color-warning, #d97706); }
.agentStatusText.danger { color: var(--color-danger); }

.badges { display: flex; flex-wrap: wrap; gap: var(--space-1); }
.outcomePill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 3px var(--space-2);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-full);
  background: var(--color-bg-app);
  color: var(--color-text-tertiary);
  font-size: var(--fs-xs);
  font-weight: var(--fw-medium);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
}
.outcomePill.clickable:hover { border-color: var(--color-accent); background: var(--color-bg-surface); }
.outcomePill.hasColor { font-weight: var(--fw-semibold); }
.outcomePill .pillIcon { width: 14px; height: 14px; flex-shrink: 0; }

.emptyLabel {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  font-style: italic;
}
</style>
